/**
 * ConfidenceGauge.tsx - Circular gauge for confidence score
 *
 * Uses Recharts RadialBarChart to display overall confidence.
 * Per wobbly-nibbling-chipmunk.md Part 15.5.3
 */

import { RadialBarChart, RadialBar, ResponsiveContainer, PolarAngleAxis } from 'recharts'
import type { ConfidenceLevel } from '@models/oracle'
import './ConfidenceGauge.css'

interface ConfidenceGaugeProps {
  score: number       // 0.0 - 1.0
  level: ConfidenceLevel
}

// Confidence level colors per spec Part 15.5.1
const LEVEL_COLORS: Record<ConfidenceLevel, string> = {
  HIGH: '#10b981',        // Green
  MEDIUM: '#f59e0b',      // Amber
  LOW: '#f97316',         // Orange
  INSUFFICIENT: '#ef4444', // Red
}

function ConfidenceGauge({ score, level }: ConfidenceGaugeProps) {
  const percentage = Math.round(score * 100)
  const color = LEVEL_COLORS[level]

  const data = [
    {
      name: 'confidence',
      value: percentage,
      fill: color,
    },
  ]

  return (
    <div className="confidence-gauge">
      <div className="gauge-container">
        <ResponsiveContainer width="100%" height={160}>
          <RadialBarChart
            cx="50%"
            cy="50%"
            innerRadius="60%"
            outerRadius="90%"
            barSize={12}
            data={data}
            startAngle={180}
            endAngle={0}
          >
            <PolarAngleAxis
              type="number"
              domain={[0, 100]}
              angleAxisId={0}
              tick={false}
            />
            <RadialBar
              background={{ fill: '#333' }}
              dataKey="value"
              cornerRadius={6}
            />
          </RadialBarChart>
        </ResponsiveContainer>

        <div className="gauge-center">
          <span className="percentage" style={{ color }}>
            {percentage}%
          </span>
          <span className="level" style={{ color }}>
            {level}
          </span>
        </div>
      </div>
    </div>
  )
}

export default ConfidenceGauge
